import { computed, reactive, ref } from "vue"
import { useQuery } from "@tanstack/vue-query"
import { isValidUUID } from "@/utils/utils"
import { useAsclepius } from "@/composables/api/asclepius"
import {
  ListMemberResponse,
  Member,
  MemberApiListMembersRequest,
  RetrieveMemberResponse
} from "@vitable/asclepius-api-client-ts"
import { PageData } from "@common/types"

export function useGetMember2(memberId: string) {
  const { getMemberApi, callWithResponse } = useAsclepius()

  async function getMember(): Promise<Member> {
    const data: RetrieveMemberResponse = await callWithResponse(() => getMemberApi().retrieveMember({ memberId }))
    return data.member
  }

  return useQuery({
    queryKey: ["members", memberId],
    queryFn: getMember
  })
}

export function useSearchMembers(options?: MemberApiListMembersRequest) {
  const { getMemberApi, callWithResponse } = useAsclepius()

  const page = ref<number>(options?.page ?? 1)
  const pageSize = ref<number>(options?.limit ?? 20)
  const companyId = ref<string | undefined>(options?.companyId)
  const memberId = ref<string | undefined>(options?.memberId)
  const userId = ref<string | undefined>(options?.userId)
  const dateOfBirth = ref<string | undefined>(options?.dateOfBirth)
  const name = ref<string | undefined>(options?.name)
  const email = ref<string | undefined>(options?.email)
  const phone = ref<string | undefined>(options?.phone)

  const limitedSearchFiltersAppliedIn = computed<boolean>(() => {
    return (
      ((memberId.value?.trim() ?? "") != "" && isValidUUID(memberId.value!)) ||
      (name.value?.trim() ?? "") != "" ||
      (email.value?.trim() ?? "") != "" ||
      (phone.value?.trim() ?? "") != "" ||
      (companyId.value?.trim() ?? "") != "" ||
      (dateOfBirth.value?.trim() ?? "") != ""
    )
  })

  async function searchMembers(): Promise<PageData<Member>> {
    if (!limitedSearchFiltersAppliedIn.value) {
      return { count: 0, results: [] }
    }

    const data: ListMemberResponse = await callWithResponse(() =>
      getMemberApi().listMembers({
        companyId: companyId.value,
        memberId: memberId.value,
        userId: userId.value,
        dateOfBirth: dateOfBirth.value,
        name: name.value,
        email: email.value,
        phone: phone.value,
        page: page.value,
        limit: pageSize.value
      })
    )

    return { count: data.count, results: data.results }
  }

  const query = useQuery({
    queryKey: ["members", page, pageSize, companyId, memberId, userId, dateOfBirth, name, email, phone],
    queryFn: searchMembers,
    initialData: { count: 0, results: [] } as PageData<Member>
  })

  return {
    query,
    page,
    pageSize,
    companyId,
    memberId,
    userId,
    dateOfBirth,
    name,
    email,
    phone,
    limitedSearchFiltersAppliedIn
  }
}

/**
 * @deprecated TODO use {@link useGetMember2} instead!
 */
export function useGetMember() {
  const member = ref<Member>()

  const { getMemberApi, callWithResponse, callResult } = useAsclepius()

  async function getMember(memberId: string): Promise<void> {
    const response = await callWithResponse<RetrieveMemberResponse>(() => getMemberApi().retrieveMember({ memberId }))
    member.value = response.member
  }

  return {
    getMember,
    getMemberResult: callResult,
    member
  }
}

/**
 * @deprecated TODO use {@link useSearchMembers} instead!
 */
export function useListMembers() {
  const members = reactive<Page<Member>>({
    currentPage: 1,
    next: null,
    previous: null,
    count: 0,
    results: []
  })

  const { getMemberApi, callWithResponse, callResult } = useAsclepius()

  async function listMembers(request: MemberApiListMembersRequest) {
    const modifiedRequest: MemberApiListMembersRequest = {
      ...request,
      page: members.currentPage
    }

    const response = await callWithResponse<ListMemberResponse>(() => getMemberApi().listMembers(modifiedRequest))

    members.next = response.next
    members.previous = response.previous
    members.count = response.count
    members.results = response.results
  }

  return {
    listMembers,
    listMembersResult: callResult,
    members
  }
}
